import { DateTime } from "luxon";
import * as LiveSwitchActions from "../../../LiveSwitch/liveSwitchActions";
import { assureSessionJsonStructure } from "../../../Shared/apiUtils";
import * as sessionService from "../../../Shared/Auto/ApiDefinitions/ClientSessionService";
import { AccessInfo, Chat, ClientSession, HeartBeatResult, LiveSwitchTokenResult, QueuePlacementResult, Registration, SetScheduleResult } from "../../../Shared/GeneratedTypes";
import { NotificationInfo } from "../../../Shared/sharedTypes";
import * as types from "./actionTypes";
import { apiCallError, beginApiCall } from "./apiStatusActions";
import { AppThunk } from "./AppThunk";
import { enqueueSnackbar } from "../../../Shared/Redux/Actions/notificationActions";

export function getSessionInfo(): AppThunk {
    return function (dispatch, getState) {
        dispatch(beginApiCall());
        let state = getState();

        return sessionService
            .getSessionInfo(state.accessInfo.token)
            .then((session) => {
                dispatch(loadGuideStreamByIdSuccess(assureSessionJsonStructure(session, state.accessInfo.cultureCode)));
            })
            .catch((error) => {
                dispatch(apiCallError());
                dispatch(errorWithTokenOrSession());
            });
    };
}

function errorWithTokenOrSession() {
    return {
        type: types.ERROR_LOADING_GUIDESTREAM as typeof types.ERROR_LOADING_GUIDESTREAM,
    };
}

function loadGuideStreamByIdSuccess(session: ClientSession) {
    return {
        type: types.LOAD_CURRENT_MENTORS_BY_ID_SUCCESS as typeof types.LOAD_CURRENT_MENTORS_BY_ID_SUCCESS,
        session,
    };
}

export function getLiveSwitchToken(): AppThunk {
    return function (dispatch, getState) {
        dispatch(beginApiCall());
        const state = getState();
        return sessionService
            .liveSwitchToken(state.deviceId, state.accessInfo.token)
            .then((result) => {
                dispatch(getLiveSwitchTokenSuccess(result));
            })
            .catch((error) => {
                dispatch(apiCallError());
                throw error;
            });
    };
}

function getLiveSwitchTokenSuccess(result: LiveSwitchTokenResult) {
    return {
        type: types.GET_LIVESWITCH_TOKEN_SUCCESS as typeof types.GET_LIVESWITCH_TOKEN_SUCCESS,
        result,
    };
}

export function endPreviousClientRegistration(): AppThunk {
    return function (dispatch, getState) {

        const state = getState();
        if (!(state.accessInfo && state.accessInfo.token)) {
            return Promise.resolve();
        }

        var previousDeviceId = localStorage.getItem("previousClientDeviceId");
        localStorage.setItem("previousClientDeviceId", state.deviceId);

        if (previousDeviceId) {
            console && console.log("Ending previous client registration");
            return sessionService.endClientRegistration(previousDeviceId, state.accessInfo.sessionId, state.accessInfo.token);
        }

        return Promise.resolve();
    };
}

export function endClientRegistration(): AppThunk {
    return function (dispatch, getState) {
        const state = getState();
        return sessionService.endClientRegistration(state.deviceId, state.order.id, state.accessInfo.token);
    };
}

export function endClientSession(): AppThunk {
    return function (dispatch, getState) {
        dispatch(LiveSwitchActions.sendSessionEnded());
        dispatch(beginApiCall());

        const state = getState();
        return sessionService
            .endClientSession(state.accessInfo.token)
            .then(() => {
                dispatch(endClientSessionSuccess(new Date()));
            })
            .catch((error) => {
                dispatch(apiCallError());
                throw error;
            });
    };
}

function endClientSessionSuccess(endDate: Date) {
    return {
        type: types.END_CLIENT_SESSION_SUCCESS as typeof types.END_CLIENT_SESSION_SUCCESS,
        endDate,
    };
}

export function endClientSessionForLater(): AppThunk {
    return function (dispatch, getState) {
        dispatch(beginApiCall());
        const state = getState();
        return sessionService
            .endClientSessionAndContinueLater(state.deviceId, state.order.id, state.accessInfo.token)
            .then(() => {
                dispatch(endClientSessionForLaterSuccess(new Date()));
            })
            .catch((error) => {
                dispatch(apiCallError());
                throw error;
            });
    };
}
function endClientSessionForLaterSuccess(endDate: Date) {
    return {
        type: types.END_CLIENT_SESSION_FOR_LATER_SUCCESS as typeof types.END_CLIENT_SESSION_FOR_LATER_SUCCESS,
        endDate,
    };
}

export function removeClientFromSession(): AppThunk {
    return function (dispatch, getState) {
        const state = getState();
        return sessionService
            .removeClientFromSession(state.deviceId, state.accessInfo.token)
            .catch((error) => {
                dispatch(apiCallError());
                throw error;
            });
    };
}
export function getAccessInfoForQuickCode(quickCode: string): AppThunk {
    return function (dispatch) {
        dispatch(beginApiCall());
        return sessionService
            .token(quickCode)
            .then((accessInfo) => {
                dispatch(getAccessInfoForQuickCodeSuccess(accessInfo));
            })
            .catch((error) => {
                dispatch(apiCallError());
                dispatch(errorWithTokenOrSession());
            });
    };
}

function getAccessInfoForQuickCodeSuccess(accessInfo: AccessInfo) {
    return {
        type: types.LOAD_ACCESS_INFO_SUCCESS as typeof types.LOAD_ACCESS_INFO_SUCCESS,
        accessInfo,
    };
}
export function setCurrentPosition(position: Position): AppThunk {
    return function (dispatch) {
        return dispatch(currentPositionUpdated(position));
    };
}

function currentPositionUpdated(position: Position) {
    return {
        type: types.POSITION_UPDATED as typeof types.POSITION_UPDATED,
        position,
    };
}

export function requestPhoto(photoId: string, mentorId: string): AppThunk {
    return function (dispatch, getState) {
        dispatch(requestPhotoOptmistic(photoId, mentorId, getState().localizations.takingPhoto));
        return dispatch(LiveSwitchActions.takePhoto(photoId));
    };
}

function requestPhotoOptmistic(photoId: string, mentorId: string, localizedNotificationMessage: string) {
    return {
        type: types.REQUEST_PHOTO as typeof types.REQUEST_PHOTO,
        photoId,
        mentorId,
        localizedNotificationMessage,
    };
}

export function acceptPrivacyStatement(): AppThunk {
    return function (dispatch, getState) {
        let state = getState();
        if (state.accessInfo) {
            return sessionService
                .acceptPrivacyStatement(state.accessInfo.token)
                .then(() => {
                    dispatch(privacyStatementAccepted());
                })
                .catch((error) => {
                    dispatch(apiCallError());
                    throw error;
                });
        }
    };
}

function privacyStatementAccepted() {
    return {
        type: types.PRIVACY_STATEMENT_ACCEPTED as typeof types.PRIVACY_STATEMENT_ACCEPTED,
    };
}

export function getActiveObservers(): AppThunk {
    return function (dispatch, getState) {
        let state = getState();
        if (state.accessInfo) {
            return sessionService
                .getActiveObservers(state.accessInfo.token)
                .then((result) => {
                    dispatch(getActiveObserversDone(result));
                })
                .catch((error) => {
                    dispatch(apiCallError());
                    throw error;
                });
        }
    };
}

function getActiveObserversDone(activeObservers: Registration[]) {
    return {
        type: types.SESSION_HAS_OBSERVER_COMPLETE as typeof types.SESSION_HAS_OBSERVER_COMPLETE,
        activeObservers,
    };
}

export function getActiveMentee(): AppThunk {
    return function (dispatch, getState) {
        let state = getState();
        if (state.accessInfo) {
            return sessionService
                .hasActiveMentee(state.deviceId, state.accessInfo.token)
                .then((result) => {
                    dispatch(getActiveMenteeDone(result));
                })
                .catch((error) => {
                    dispatch(apiCallError());
                    throw error;
                });
        }
    };
}

function getActiveMenteeDone(activeMentee: boolean) {
    return {
        type: types.SESSION_HAS_ACTIVE_MENTEE as typeof types.SESSION_HAS_ACTIVE_MENTEE,
        activeMentee,
    };
}

export function heartbeatPing(positions: Position[] | null): AppThunk {
    return function (dispatch, getState) {
        //We are going to execute behind the scenes silently
        //dispatch(beginApiCall());
        let state = getState();
        if (state.accessInfo) {
            return sessionService
                .heartBeatPing(positions as any, state.accessInfo.token) // allow null since it is a valid usecase on this service
                .then((result) => {
                    dispatch(hearbeatPingDone(state.deviceId, result));
                })
                .catch((error) => {
                    dispatch(apiCallError());
                    throw error;
                });
        }
    };
}

function hearbeatPingDone(deviceId: string, heartBeatResult: HeartBeatResult) {
    return {
        type: types.HEARTBEAT_COMPLETE as typeof types.HEARTBEAT_COMPLETE,
        deviceId: deviceId,
        heartBeatResult: heartBeatResult
    };
}

export function setPolicyHolderPhoto(photo: Blob): AppThunk {
    return function (dispatch, getState) {
        dispatch(beginApiCall());
        let state = getState();
        return sessionService
            .addPolicyHolderPhoto(photo, state.accessInfo.token)
            .then((url) => {
                dispatch(policyHolderPhotoSuccess(url.url!));
            })
            .catch((error) => {
                dispatch(apiCallError());
                throw error;
            });
    };
}

function policyHolderPhotoSuccess(url?: string) {
    return {
        type: types.POLICY_HOLDER_PHOTO_SUCCESS as typeof types.POLICY_HOLDER_PHOTO_SUCCESS,
        url,
    };
}

export function optOutOfPortaitPhoto(): AppThunk {
    return function (dispatch, getState) {
        dispatch(beginApiCall());
        let state = getState();
        return sessionService
            .optOutOfPortraitPhoto(state.accessInfo.token)
            .then(() => {
                dispatch(optOutOfPortaitPhotoSuccess());
            })
            .catch((error) => {
                dispatch(apiCallError());
                throw error;
            });
    };
}

function optOutOfPortaitPhotoSuccess() {
    return {
        type: types.OPT_OUT_OF_PORTRAIT_PHOTO_SUCCESS as typeof types.OPT_OUT_OF_PORTRAIT_PHOTO_SUCCESS,
    };
}

export function setScheduleDateTime(dateTimeUtc: DateTime): AppThunk {
    return function (dispatch, getState) {
        let state = getState();
        return sessionService
            .setScheduleDateTime(dateTimeUtc.toJSDate(), state.accessInfo.token)
            .then(() => {
                dispatch(scheduleDateTimeSetOptimistic(dateTimeUtc));
            })
            .catch((error) => {
                dispatch(scheduleDateTimeSetFailed(error.internalErrorMessage));
                throw error;
            });
    };
}

function scheduleDateTimeSetOptimistic(date: DateTime) {
    return {
        type: types.SCHEDULE_SET_OPTIMISTIC as typeof types.SCHEDULE_SET_OPTIMISTIC,
        date,
    };
}

function scheduleDateTimeSetFailed(result: SetScheduleResult) {
    return {
        type: types.SCHEDULE_SET_FAILED as typeof types.SCHEDULE_SET_FAILED,
        result
    };
}


export function clearPolicyHolderPhotoUrlLocally(): AppThunk {
    return function (dispatch) {
        dispatch(policyHolderPhotoCleared());
    };
}

function policyHolderPhotoCleared() {
    return {
        type: types.POLICY_HOLDER_PHOTO_CLEARED as typeof types.POLICY_HOLDER_PHOTO_CLEARED,
        url: undefined,
    };
}

export function enterQueue(): AppThunk {
    return function (dispatch, getState) {
        dispatch(beginApiCall());
        let state = getState();
        return sessionService
            .enterQueue(state.deviceId, state.accessInfo.token)
            .then((placeInQueueResult) => {
                dispatch(enteredIntoQueue(placeInQueueResult));
            })
            .catch((error) => {
                dispatch(apiCallError());
                throw error;
            });
    };
}

function enteredIntoQueue(queuePlacementResult: QueuePlacementResult) {
    return {
        type: types.ENTERED_INTO_QUEUE_SUCCESS as typeof types.ENTERED_INTO_QUEUE_SUCCESS,
        queuePlacementResult,
    };
}

export function checkQueuePlacement(): AppThunk {
    return function (dispatch, getState) {
        dispatch(beginApiCall());
        let state = getState();
        return sessionService
            .checkQueuePlacement(state.accessInfo.token)
            .then((queuePlacementResult) => {
                dispatch(checkQueuePlacementSuccess(queuePlacementResult));
            })
            .catch((error) => {
                dispatch(apiCallError());
                throw error;
            });
    };
}

function checkQueuePlacementSuccess(queuePlacementResult: QueuePlacementResult) {
    return {
        type: types.CHECK_QUEUE_PLACEMENT_SUCCESS as typeof types.CHECK_QUEUE_PLACEMENT_SUCCESS,
        queuePlacementResult,
    };
}
export function getScheduleSlots(startDate: DateTime, endDate: DateTime): AppThunk {
    return function (dispatch, getState) {
        let state = getState();
        return sessionService
            .getAvailableSchedule({ startTimeUTC: startDate.toJSDate() }, state.accessInfo.token)
            .then((scheduleResponse) => {
                let slots = scheduleResponse.timeSlots as any[];
                dispatch(
                    getScheduleSlotsSuccess(
                        slots.map((d) => DateTime.fromISO(d)),
                        startDate,
                        endDate
                    )
                );
            })
            .catch((error) => {
                //dispatch(apiCallError());
                throw error;
            });
    };
}

function getScheduleSlotsSuccess(slots: DateTime[], startDate: DateTime, endDate: DateTime) {
    return {
        type: types.GET_SCHEDULESLOTS_SUCCESS_SILENT as typeof types.GET_SCHEDULESLOTS_SUCCESS_SILENT,
        slots,
        startDate,
        endDate,
    };
}
export function saveFeedback(feedback: string): AppThunk {
    return function (dispatch, getState) {
        let state = getState();
        return sessionService
            .submitFeedback(feedback, state.accessInfo.token)
            .then(() => {
                dispatch(saveFeedbackOptimisticComplete(feedback, state.localizations.feedbackSubmitted));
            })
            .catch((error) => {
                dispatch(apiCallError());
                throw error;
            });
    };
}

function saveFeedbackOptimisticComplete(feedback: string, localizedNotificationMessage: string) {
    return {
        type: types.SAVE_FEEDBACK_OPTIMISTIC_COMPLETE as typeof types.SAVE_FEEDBACK_OPTIMISTIC_COMPLETE,
        feedback,
        localizedNotificationMessage,
    };
}

export function saveVideoRating(rating: number | null): AppThunk {
    return function (dispatch, getState) {
        dispatch(saveVideoRatingOptimisticComplete(rating));

        let state = getState();
        return sessionService
            .submitVideoRating(rating, state.accessInfo.token)
            .then(() => { })
            .catch((error) => {
                dispatch(apiCallError());
                throw error;
            });
    };
}

function saveVideoRatingOptimisticComplete(rating: number | null) {
    return {
        type: types.SAVE_VIDEORATING_OPTIMISTIC_COMPLETE as typeof types.SAVE_VIDEORATING_OPTIMISTIC_COMPLETE,
        rating,
    };
}

export function saveAudioRating(rating: number | null): AppThunk {
    return function (dispatch, getState) {
        dispatch(saveAudioRatingOptimisticComplete(rating));
        let state = getState();
        return sessionService
            .submitAudioRating(rating, state.accessInfo.token)
            .then(() => { })
            .catch((error) => {
                dispatch(apiCallError());
                throw error;
            });
    };
}

function saveAudioRatingOptimisticComplete(rating: number | null) {
    return {
        type: types.SAVE_AUDIORATING_OPTIMISTIC_COMPLETE as typeof types.SAVE_AUDIORATING_OPTIMISTIC_COMPLETE,
        rating,
    };
}

export function saveOverallRating(rating: number | null): AppThunk {
    return function (dispatch, getState) {
        dispatch(saveOverallRatingOptimisticComplete(rating));
        let state = getState();
        return sessionService
            .submitOverallRating(rating, state.accessInfo.token)
            .then(() => { })
            .catch((error) => {
                dispatch(apiCallError());
                throw error;
            });
    };
}

function saveOverallRatingOptimisticComplete(rating: number | null) {
    return {
        type: types.SAVE_OVERALLRATING_OPTIMISTIC_COMPLETE as typeof types.SAVE_OVERALLRATING_OPTIMISTIC_COMPLETE,
        rating,
    };
}

export function updateCulture(cultureCode: string): AppThunk {
    return function (dispatch, getState) {
        dispatch(beginApiCall());
        let state = getState();
        return sessionService
            .updateCulture(cultureCode, state.accessInfo.token)
            .then((session) => {
                dispatch(updateCultureComplete(assureSessionJsonStructure(session, null)));
            })
            .catch((error) => {
                dispatch(apiCallError());
                dispatch(
                    enqueueSnackbar({
                        message: state.localizations.failedToUpdateGuideStreamCulture,
                        key: new Date().getTime() + Math.random(),
                        options: {
                            variant: "error",
                        },
                    } as NotificationInfo)
                );
            });
    };
}

function updateCultureComplete(session: ClientSession) {
    return {
        type: types.UPDATE_CULTURE_SUCCESS as typeof types.UPDATE_CULTURE_SUCCESS,
        session,
    };
}

export function sendChat(chat: Chat): AppThunk {
    return function (dispatch, getState) {
        let state = getState().order;
        if (state) {
            return dispatch(LiveSwitchActions.sendChat(chat));
        } else {
            return dispatch(apiCallError());
        }
    };
}

export type CurrentMentorsActions =
    | ReturnType<typeof loadGuideStreamByIdSuccess>
    | ReturnType<typeof policyHolderPhotoSuccess>
    | ReturnType<typeof requestPhotoOptmistic>
    | ReturnType<typeof hearbeatPingDone>
    | ReturnType<typeof policyHolderPhotoCleared>
    | ReturnType<typeof enteredIntoQueue>
    | ReturnType<typeof checkQueuePlacementSuccess>
    | ReturnType<typeof getAccessInfoForQuickCodeSuccess>
    | ReturnType<typeof currentPositionUpdated>
    | ReturnType<typeof privacyStatementAccepted>
    | ReturnType<typeof errorWithTokenOrSession>
    | ReturnType<typeof scheduleDateTimeSetOptimistic>
    | ReturnType<typeof getLiveSwitchTokenSuccess>
    | ReturnType<typeof endClientSessionSuccess>
    | ReturnType<typeof saveFeedbackOptimisticComplete>
    | ReturnType<typeof saveAudioRatingOptimisticComplete>
    | ReturnType<typeof saveVideoRatingOptimisticComplete>
    | ReturnType<typeof getScheduleSlotsSuccess>
    | ReturnType<typeof saveOverallRatingOptimisticComplete>
    | ReturnType<typeof updateCultureComplete>
    | ReturnType<typeof endClientSessionForLaterSuccess>
    | ReturnType<typeof getActiveObserversDone>
    | ReturnType<typeof getActiveMenteeDone>
    | ReturnType<typeof optOutOfPortaitPhotoSuccess>
    | ReturnType<typeof scheduleDateTimeSetFailed>;
